import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Layers, Loader2 } from 'lucide-react';
import { SectorAnalysisReport } from '@/components/SectorAnalysisReport';
import { sectorAnalysisService, type SectorAnalysisPayload } from '@/services/api';

export function SectorAnalysisPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<SectorAnalysisPayload | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const response = await sectorAnalysisService.getLatest();
      if (cancelled) return;
      if (!response?.analysis) {
        setError('No sector analysis is available yet.');
        setAnalysis(null);
      } else {
        setAnalysis(response.analysis);
      }
      setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="sector-analysis-page flex min-h-screen flex-col bg-gray-50 print:bg-white">
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="sector-analysis-page__header shrink-0 border-b border-gray-200 bg-white print:hidden"
      >
        <div className="mx-auto flex w-full max-w-7xl items-center gap-4 px-6 py-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-600">
              <Layers className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="tracking-tight text-gray-900">Sector Analysis</h2>
              <p className="truncate text-sm text-gray-500">
                {analysis?.proxy_label ?? 'SOX / semiconductors'}
              </p>
            </div>
          </div>
        </div>
      </motion.header>

      <main className="sector-analysis-page__main mx-auto w-full max-w-7xl flex-1 px-6 py-8 md:py-10 print:max-w-none print:p-0">
        <div className="sector-analysis-page__shell overflow-hidden rounded-3xl border border-gray-200 bg-white print:overflow-visible print:rounded-none print:border-0 print:shadow-none">
          {loading ? (
            <div className="flex items-center justify-center gap-3 px-6 py-24">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="text-gray-500">Loading sector analysis…</span>
            </div>
          ) : error ? (
            <div className="px-6 py-24 text-center">
              <p className="text-gray-600">{error}</p>
            </div>
          ) : analysis ? (
            <SectorAnalysisReport analysis={analysis} />
          ) : null}
        </div>
      </main>
    </div>
  );
}
